/**
 * Section « Vue d'ensemble ».
 *
 * Le sélecteur de période cadre tout ce qui est en dessous : chaque tuile,
 * chaque graphique et le tableau sont rendus sur la MÊME tranche de données,
 * donc les chiffres s'accordent toujours entre eux — et avec ceux des autres
 * sections, qui lisent la même période dans l'URL.
 *
 * ── Deux exceptions, assumées et annoncées ─────────────────────────────────
 *
 * 1. « Rétention J+7 » et « Rétention J+30 » changent le SENS de la période :
 *    elles portent sur la COHORTE — les comptes dont la première action tombe
 *    dans l'intervalle, suivis ensuite sur tout l'historique — et non sur les
 *    comptes actifs pendant la fenêtre, comme tout le reste de la page. C'est
 *    une nécessité : la rétention a besoin de la date d'entrée de chaque
 *    compte. La précision de chaque tuile l'annonce, et la note de bas de page
 *    l'explique.
 *
 *    Elles viennent d'une requête séparée (`/engagement`), pas d'un champ de
 *    plus dans `/period` : la rétention part d'un agrégat par compte sur TOUT
 *    l'historique de `user_actions`, le seul balayage non borné par la période.
 *    L'ajouter à `/period` le ferait payer à toutes les pages qui l'appellent.
 *    La page ne bloque pas dessus — les deux tuiles se remplissent après.
 *
 * 2. « Pénétration de la période » est bornée des DEUX côtés : les comptes
 *    CRÉÉS dans la fenêtre, et parmi eux ceux abonnés aujourd'hui. À ne pas
 *    confondre avec la « Pénétration actuelle » de la section Abonnement, qui
 *    rapporte les abonnés à TOUTE la base — les deux chiffres diffèrent, et
 *    c'est normal.
 *
 *    ⚠️ Un taux de cohorte mûrit : un compte créé avant-hier n'a pas eu le
 *    temps de s'abonner, donc une fenêtre récente et courte sort
 *    structurellement plus bas. Ce n'est pas une dégradation, c'est un manque
 *    de recul — et c'est pourquoi la comparaison année/année, qui confronte
 *    deux fenêtres de même âge relatif, est la plus fiable des deux.
 *
 *    C'est la seule tuile colorée : ses seuils de métier (35 % / 40 %) sont
 *    dans `@shared/types/stats`, et la couleur vient toujours avec son icône
 *    et son libellé en clair. C'est aussi la seule à porter deux comparaisons —
 *    période/période et année/année, en relatif avec l'écart en points à côté.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { AccountsChart } from '../components/AccountsChart';
import { ActionBreakdownChart } from '../components/ActionBreakdownChart';
import { ActionsPerDayChart } from '../components/ActionsPerDayChart';
import { ActiveUsersChart } from '../components/ActiveUsersChart';
import { ActivityHeatmap } from '../components/ActivityHeatmap';
import { MarkerSummaryCard } from '../components/MarkerSummaryCard';
import { SectionShell } from '../components/SectionShell';
import { StatTile } from '../components/StatTile';
import { SubscribersChart } from '../components/SubscribersChart';
import { SubscriptionSegmentsChart } from '../components/SubscriptionSegmentsChart';
import { TopTracksTable } from '../components/TopTracksTable';
import { useAuth } from '../hooks/useAuth';
import { useMarkerRecap } from '../hooks/useMarkerRecap';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { useEngagementStats, usePeriodStats } from '../hooks/usePeriodStats';
import { penetrationStatus, penetrationStatusLabel } from '@shared/types/stats';

import type { StatDelta } from '../components/StatTile';

import { dataPeriod, daysBetween } from '../lib/dates';
import { formatDecimal, formatDuration, formatInteger, formatPercent } from '../lib/format';
import { withMissingDays } from '../lib/series';
import { pointsVerdict } from '../lib/verdict';

export function DashboardPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  // La periode vit dans la query string : les liens de la carte des marqueurs
  // doivent la conserver, sinon la section Marqueurs s'ouvre sur une autre
  // periode et le verdict change sous les yeux du lecteur.
  const { search } = useLocation();

  const { data, error, isPending, isFetching } = usePeriodStats({
    from: period.from,
    to: period.to,
    token,
    // Un jeton expire (30 jours) ou revoque doit ramener a la page de connexion
    // plutot que d'afficher une erreur que l'utilisateur ne peut pas resoudre.
    onUnauthorized: signOut,
  });

  // Requete SEPAREE, et non un champ de plus dans /period : la retention part
  // d'un agregat par compte sur TOUT l'historique de user_actions, le seul
  // balayage du dashboard qui ne soit pas borne par la periode. L'ajouter a
  // /period le ferait payer a toutes les pages qui l'appellent.
  //
  // Les deux requetes partent en parallele et l'API les met en cache 5 minutes
  // chacune : ouvrir ensuite l'onglet Retention tombe sur un cache chaud.
  const engagement = useEngagementStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const d7 = engagement.data?.retention.find((point) => point.day === 7);
  const d30 = engagement.data?.retention.find((point) => point.day === 30);

  /**
   * Précision d'une tuile de rétention.
   *
   * La page NE BLOQUE PAS sur cette requête : `SectionShell` ne reçoit que
   * l'état de `/period`. Les deux tuiles affichent un tiret puis se
   * remplissent — un dashboard entier retenu par son agrégat le plus lourd
   * serait un mauvais échange.
   *
   * Un échec est dit, pas avalé : le tiret vient avec « indisponible ».
   */
  const retentionHint = (point: typeof d7): string => {
    if (engagement.error) return 'indisponible — le calcul de rétention a échoué';
    if (!point) return 'cohorte de la période — calcul en cours';
    return `cohorte de la période — ${formatInteger(point.eligibleUsers)} comptes éligibles`;
  };

  const summary = data?.summary;
  // Période couverte par les DONNÉES : la sélection peut courir jusqu'à la fin
  // du mois, les données d'usage s'arrêtent à hier (sync nocturne).
  const effective = dataPeriod(period, 'nightly');
  const days = daysBetween(effective.from, effective.to);

  // Le niveau se calcule sur le taux, pas sur un champ d'API : les seuils sont
  // un arbitrage d'equipe, ils n'ont rien a faire dans la reponse du serveur.
  const penetration = data?.penetration;
  const penetrationPercent = penetration?.current?.percent ?? 0;
  const penetrationLevel = penetrationStatus(penetrationPercent);

  /**
   * Une ligne de comparaison du taux de pénétration.
   *
   * Le RELATIF mène et le POINT suit, parce que les deux sont justes et ne
   * disent pas la même chose : passer de 21 % à 22,2 % c'est +5,7 % en relatif
   * et +1,2 point. Le relatif rend deux périodes comparables entre elles, le
   * point dit de combien le taux a réellement bougé — et « +5,7 % » tout seul
   * se fait lire comme 5,7 points par la moitié des lecteurs.
   *
   * Le VERDICT se décide sur les POINTS, pas sur le relatif : c'est l'écart
   * réel qui dit si ça a bougé. Sous un dixième de point on lit l'arrondi des
   * snapshots, et la ligne reste neutre plutôt que de verdir sur du bruit.
   *
   * Une fenêtre sans aucun snapshot rend « aucun relevé » : c'est différent de
   * « pas de variation », et le taire laisserait croire à une stabilité que
   * personne n'a mesurée.
   */
  const comparison = (
    label: string,
    relative: number | null | undefined,
    pointsDelta: number | null | undefined,
    reference: { from: string; to: string } | null | undefined,
  ): StatDelta => {
    if (!reference) {
      // Aucun compte cree dans la fenetre : il n'y a pas de taux a comparer.
      // Different de « pas de variation », et le taire laisserait croire a une
      // stabilite que personne n'a mesuree.
      return { text: `aucun compte sur ${label}`, verdict: 'insufficient', higherIsBetter: true };
    }

    const signed = (value: number) => `${value >= 0 ? '+' : '−'}${formatDecimal(Math.abs(value))}`;

    return {
      text:
        relative !== null && relative !== undefined
          ? `${signed(relative)} % vs ${label}`
          : `vs ${label}`,
      verdict: pointsVerdict(pointsDelta ?? null),
      // Plus d'abonnes payants pour un meme nombre de comptes est une bonne
      // nouvelle : la hausse est favorable.
      higherIsBetter: true,
      ...(pointsDelta !== null && pointsDelta !== undefined
        ? { note: `${signed(pointsDelta)} pt` }
        : {}),
    };
  };

  // Les deux graphiques de snapshots partagent la meme serie densifiee : les
  // jours non mesures y sont des points nuls, pour que le trait s'interrompe
  // au lieu d'interpoler par-dessus le trou.
  const accounts = useMemo(
    () => withMissingDays(data?.accountsTimeline ?? [], effective.from, effective.to),
    [data?.accountsTimeline, effective.from, effective.to],
  );

  // Meme recapitulatif que la section Marqueurs, tronque aux plus recents :
  // « ce qu'on a change, ca a servi a quoi ? » se pose en arrivant ici, pas
  // apres un detour par un onglet de gestion.
  const markerRecap = useMarkerRecap(data, effective.from, effective.to, 'analytics');

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
    >
      <div className="kpi-row">
        <StatTile
          label="Actions"
          value={formatInteger(summary?.totalActions ?? 0)}
          hint={`sur ${formatInteger(days)} jour${days > 1 ? 's' : ''}`}
        />
        <StatTile
          label="Utilisateurs actifs"
          value={formatInteger(summary?.activeUsers ?? 0)}
          hint="comptes distincts sur la période"
        />
        <StatTile
          label="Nouveaux comptes"
          value={formatInteger(summary?.newAccounts ?? 0)}
          hint="inscriptions sur la période"
        />
        <StatTile
          label="Temps de pratique"
          value={formatDuration(summary?.practiceSeconds ?? 0)}
          hint="cumul des durées d'action"
        />
        <StatTile
          label="Actions par actif"
          value={formatDecimal(summary?.avgActionsPerActiveUser ?? 0)}
          hint="moyenne sur la période"
        />

        {/* Retention. ⚠️ Ces deux tuiles changent le SENS de la periode : elles
            portent sur la COHORTE (les comptes dont la premiere action tombe
            dans l'intervalle), pas sur les comptes actifs pendant la periode.
            C'est une necessite — la retention a besoin de la date d'entree de
            chaque compte — et la precision de chaque tuile le dit.

            Memes chiffres que la rangee de l'onglet Retention, a la virgule :
            meme requete, meme cache. */}
        <StatTile
          label="Rétention J+7"
          value={d7 ? formatPercent(d7.ratePercent) : '—'}
          hint={retentionHint(d7)}
        />
        <StatTile
          label="Rétention J+30"
          value={d30 ? formatPercent(d30.ratePercent) : '—'}
          hint={retentionHint(d30)}
        />

        {/* KPI d'abonnement, cale sur la PERIODE des DEUX cotes : les comptes
            CREES dans la fenetre, et parmi eux ceux abonnes aujourd'hui.
            `daily_snapshots.total_accounts` est un cumul depuis le premier
            jour du produit — un taux calcule dessus ne dependait que de la
            borne haute, et deplacer la date de debut n'y changeait rien.

            ⚠️ Un taux de cohorte MURIT : un compte cree avant-hier n'a pas eu
            le temps de s'abonner. Periode/periode compare donc deux fenetres
            d'ages differents et sous-estime la recente ; annee/annee confronte
            deux fenetres de meme age relatif, c'est la comparaison fiable.

            La carte prend la couleur de son niveau : rouge sous 35 %, orange
            entre 35 et 40, vert au-dela. Les seuils vivent dans
            `@shared/types/stats`, pour qu'un seul endroit les porte. */}
        <StatTile
          label="Pénétration de la période"
          value={penetration?.current ? formatPercent(penetrationPercent) : '—'}
          status={{
            level: penetrationLevel,
            label: penetrationStatusLabel(penetrationLevel),
          }}
          deltas={
            // Sur une periode d'un an ou plus, « periode precedente » et « meme
            // periode l'an dernier » designent la MEME tranche — sur 365 jours
            // elles sont rigoureusement identiques. Deux lignes portant le meme
            // nombre se liraient comme deux mesures qui se confirment l'une
            // l'autre : on n'en montre qu'une, et on dit pourquoi.
            penetration?.comparisonsOverlap
              ? [
                  comparison(
                    'les 12 mois précédents',
                    penetration.periodOverPeriodPercent,
                    penetration.periodOverPeriodPoints,
                    penetration.previous,
                  ),
                ]
              : [
                  comparison(
                    'période préc.',
                    penetration?.periodOverPeriodPercent,
                    penetration?.periodOverPeriodPoints,
                    penetration?.previous,
                  ),
                  comparison(
                    'an dernier',
                    penetration?.yearOverYearPercent,
                    penetration?.yearOverYearPoints,
                    penetration?.yearAgo,
                  ),
                ]
          }
          hint={
            penetration?.current
              ? `${formatInteger(penetration.current.totalSubscribers)} abonnés parmi les ${formatInteger(penetration.current.totalAccounts)} comptes créés sur la période`
              : 'aucun compte créé sur la période'
          }
        />
      </div>

      <div className="chart-grid">
        {/* En tete de grille : ce qui a change cadre la lecture de tout ce qui
            suit. Pleine largeur — c'est un tableau, pas un graphique. */}
        <MarkerSummaryCard recap={markerRecap} isRefetching={isFetching} search={search} />

        {/* Deux cartes et non un graphique a double axe : des dizaines de
            milliers d'actions et quelques centaines d'utilisateurs n'ont rien
            a faire sur la meme echelle. */}
        <ActionsPerDayChart data={data?.dailyActivity ?? []} />
        <ActiveUsersChart data={data?.dailyActivity ?? []} />

        <ActivityHeatmap data={data?.heatmap ?? []} />

        <ActionBreakdownChart data={data?.actionBreakdown ?? []} />
        <SubscriptionSegmentsChart data={data?.subscriptionSegments ?? []} />

        <AccountsChart data={accounts} />
        <SubscribersChart data={accounts} />

        <TopTracksTable data={data?.topTracks ?? []} />
      </div>

      <p className="footnote">
        Les comptes de test (identifiants 1 à 4) sont exclus de toutes les statistiques.{' '}
        <strong>Les deux tuiles de rétention lisent la période autrement :</strong> elles
        portent sur la <em>cohorte</em> — les comptes dont la première action tombe dans
        l'intervalle, suivis ensuite sur tout l'historique — et non sur les comptes actifs
        pendant la fenêtre, comme le reste de la page. Un compte n'entre au dénominateur d'un
        jalon que s'il a eu le temps de revenir, sinon chaque vague d'inscriptions ferait
        plonger le taux mécaniquement. Ce sont les mêmes chiffres que l'onglet Rétention, où la
        courbe complète et la comparaison abonnés / non-abonnés se trouvent.
      </p>

      <p className="footnote">
        <strong>La tuile de pénétration ne compte que les comptes créés sur la période</strong>,
        et parmi eux ceux qui sont abonnés <strong>aujourd'hui</strong>. Elle diffère donc de la
        « Pénétration actuelle » de l'onglet Abonnement, qui rapporte les abonnés à toute la
        base.{' '}
        <strong>Les comparaisons ne disent pas « le taux qu'on faisait à l'époque ».</strong> La
        prod ne conserve aucun historique d'abonnement — la ligne Stripe d'un compte est écrasée
        à chaque changement — donc le seul statut disponible est celui de maintenant. Une fenêtre
        de référence, c'est donc « les comptes nés à ce moment-là, abonnés aujourd'hui ». Comme
        une cohorte ancienne a eu bien plus de temps pour convertir qu'une cohorte récente, elle
        ressort mécaniquement plus haute : une baisse affichée est d'abord un écart de maturité,
        pas forcément une dégradation. Sur une période d'un an ou plus, « période précédente » et
        « an dernier » désignent la même tranche — une seule comparaison est alors affichée.
      </p>
    </SectionShell>
  );
}
